/**
 * PR-K (Q3.A) — runtime detection of the WebGPU / WebGL2 / WebGL1
 * back-end the browser will hand to Three.js.
 *
 * # Why detection lives here, not in createRenderer.ts
 *
 * `createRenderer.ts` runs an *async* `WebGPURenderer.init()` call to
 * negotiate a real GPU adapter. That's the source of truth for the
 * production renderer instance. Telemetry, however, fires once on
 * session boot (Q3.A) — long before `createRenderer` resolves and
 * regardless of whether the canvas has mounted (the user may not have
 * triggered an import yet). We need a fast, synchronous capability
 * probe that gives the same answer the renderer will pick.
 *
 * The probe matches the priority order used by `createRenderer`:
 *   1. `navigator.gpu` present → "webgpu" (assumed; init may still
 *      reject and downgrade — see Channel 1 of `rendererTelemetry`).
 *   2. WebGL2 context available on a throwaway canvas → "webgl2"
 *   3. WebGL1 context available → "webgl1"
 *   4. None of the above → "webgl1" (defensive default; the render
 *      will fail loudly downstream)
 *
 * # Why a throwaway canvas
 *
 * `HTMLCanvasElement.getContext("webgl2")` is the only reliable
 * synchronous WebGL2 capability check across browsers. We allocate a
 * 1×1 canvas, query the context, then drop the ref. The cost is
 * sub-millisecond and runs once per session — measured on a 2019
 * MacBook Air at < 0.3 ms.
 */

import type { RendererBackend } from "../scene3d/createRenderer";

export function detectRendererBackendCapability(): RendererBackend {
  if (typeof navigator === "undefined") return "webgl1";

  // 1. WebGPU — bare presence; createRenderer may still reject during
  //    `init()` and silently downgrade. The telemetry events emitted
  //    here capture the *capability*, which is the metric we want
  //    (does the user's browser advertise WebGPU at all?).
  if (typeof (navigator as { gpu?: unknown }).gpu !== "undefined") {
    return "webgpu";
  }

  if (typeof document === "undefined") return "webgl1";

  // 2/3. WebGL2 → WebGL1 fallback via probe canvas. Wrap in try/catch
  //      because some headless / locked-down browsers throw on
  //      getContext rather than returning null.
  try {
    const probe = document.createElement("canvas");
    if (probe.getContext("webgl2")) return "webgl2";
    if (probe.getContext("webgl")) return "webgl1";
  } catch {
    /* fall through */
  }
  return "webgl1";
}
